import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { createPortal } from "react-dom";
import CustomTable from "../../../components/table/CustomTable";
import StatusChip from "../../../components/statusChip/StatusChip";
import { formatMomentDate } from "../../../utils/dateHelper";
import { useToast } from "../../../context/TostContext";
import ConfirmModal from "../../../components/withModal/ConfirmModal";
import { reverseUserSubscription } from "../../../redux/slice/subscriptionSlice";
import { TableSearch } from "../../../components/form/Fields";
import { UserInital } from "../../../utils/otherHelopers";
import CustomSelect from "../../../components/form/CustomSelect";
import { CheckboxOption, MultiValueMessage } from "../../../components/form/utils";

const customStyles = {
    menu: (provided) => ({
        ...provided,
        minWidth: "250px",
        zIndex: 9999
    }),
    multiValueRemove: () => ({
        display: 'none',
    }),
    option: (provided, state) => ({
        ...provided,
        fontSize: "12px",
        backgroundColor: state.isSelected
            ? "transparent"
            : state.isFocused
                ? "var(--primary-100)"
                : "transparent",
        color: 'var(--text-primary)',
        ':active': {
            backgroundColor: "var(--primary-100)"
        }
    })
};



const statusOptions = [
    { value: "active", label: "Active" },
    // { value: "trialing", label: "Trialing" },
    // { value: "initialized", label: "Initialized" },
    { value: "cancelled", label: "Cancelled" },
    { value: "expired", label: "Expired" },
    { value: "revoked", label: "Revoked" },
    { value: "failed", label: "Failed" },
];

const sourceOptions = [
    { value: "mep_sync", label: "MEP Sync" },
    { value: "admin", label: "Admin" },
    { value: "cashfree", label: "Cashfree" },
    { value: "apple", label: "Apple" },
];

const getStatusBg = (status) => {
    const lowerStatus = status?.toLowerCase();
    switch (lowerStatus) {
        case "active":
            return "success";
        case "trialing":
        case "initialized":
            return "blue";
        case "cancelled":
            return "warning";
        case "expired":
            return "secondary";
        case "paid":
            return "success";
        case "revoked":
        case "failed":
            return "danger";
        case "pending":
            return "warning";
        default:
            return "";
    }
};

const parsePaymentDetails = (details, group) => {
    if (!details) return "-";

    const method = details.payment_method || details;

    if (group === "upi" && method.upi) {
        return `UPI ID: ${method.upi.upi_id || "N/A"}`;
    }
    if (group === "card" && method.card) {
        const { card_bank_name, card_network, card_type, card_number } = method.card;
        const typeFormatted = card_type ? card_type.replace('_', ' ') : '';
        return [card_bank_name, card_network, typeFormatted, `ending in ${card_number}`].filter(Boolean).join(" ").toUpperCase();
    }
    return group ? group.toUpperCase() : "-";
};

const List = ({
    data,
    searchTerm,
    setSearchTerm,
    setCurrentPage,
    currentPage,
    limit,
    setLimit,
    total,
    refresh,
    selectedStatuses,
    setSelectedStatuses,
    selectedSources,
    setSelectedSources,
    selectedPlans,
    setSelectedPlans,
    plans,
}) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const toast = useToast();
    const [showReversePopup, setShowReversePopup] = useState(false);
    const [subscriptionToReverse, setSubscriptionToReverse] = useState(null);

    const handleReverseConfirm = () => {
        if (!subscriptionToReverse) return;
        dispatch(reverseUserSubscription({ subscription_id: subscriptionToReverse.id }))
            .unwrap()
            .then((res) => {
                toast.success(res?.message || "Subscription reversed successfully");
                setShowReversePopup(false);
                setSubscriptionToReverse(null);
                refresh();
            })
            .catch((err) => {
                toast.error(err?.message || "Failed to reverse subscription");
            });
    };

    const headerPortalTarget = document.getElementById("header-portal-target");

    const headerContent = (
        <div className="d-flex align-items-center justify-content-between w-100">
            <h4 className="fw-semibold mb-0 text-primary fs-lg">
                <span className="text-muted fw-normal">Subscriptions</span>
            </h4>
        </div>
    );

    const actionList = [
        {
            title: "View",
            handler: (row) => {
                if (row?.id) {
                    navigate(`/subscriptions/${row.id}`);
                }
            }
        },
        {
            title: "Revoke Plan",
            isVisible: (row) => row.status === 'active' && row.created_by_type === 'admin',
            handler: (row) => {
                setSubscriptionToReverse(row);
                setShowReversePopup(true);
            }
        }
    ];

    const columns = [
        {
            header: "User",
            cell: (_, item) => (
                <div className="d-flex align-items-center gap-3 text-nowrap">
                    <UserInital image={item.user?.profile?.profile_photo?.url} name={`${item.user?.first_name || ""} ${item.user?.last_name || ""}`.trim()} />
                    <div>
                        <h6 className="fw-semibold fs-md">
                            {item.user ? `${item.user.first_name} ${item.user.last_name || ""}`.trim() : "-"}
                        </h6>
                        <span className="text-muted fs-sm">{item.user?.mobile_number || "-"}</span>
                        <p className="text-muted fs-sm">{item.user?.email || "-"}</p>
                    </div>
                </div>
            )
        },
        {
            header: 'Plan',
            cell: (_, item) => (
                <>
                    <p className="fw-medium text-nowrap">{item.plan?.name || "-"}</p>
                    <span className="text-muted small text-capitalize text-nowrap">{item.plan?.interval || "-"}</span>
                </>
            )
        },
        {
            header: 'Source',
            cell: (_, item) => <div className="text-capitalize text-nowrap">{item.source || "-"}</div>
        },
        {
            header: 'Is Trial',
            cell: (_, item) => (
                item.is_trial ? <StatusChip bg="blue" pill title="Yes" /> : <StatusChip bg="secondary" pill title="No" />
            )
        },
        {
            header: 'Start Date',
            cell: (_, item) => <div className="text-nowrap">{item.start_date ? formatMomentDate(item.start_date, "DD-MM-YYYY hh:mm A") : "-"}</div>
        },
        {
            header: 'Trial End Date',
            cell: (_, item) => <div className="text-nowrap">{item.trial_end_date ? formatMomentDate(item.trial_end_date, "DD-MM-YYYY hh:mm A") : "-"}</div>
        },
        {
            header: 'End Date',
            cell: (_, item) => <div className="text-nowrap">{item.end_date ? formatMomentDate(item.end_date, "DD-MM-YYYY hh:mm A") : "-"}</div>
        },
        {
            header: 'Total Paid Amount',
            cell: (_, item) => {
                const amount = item.total_amount_paid;
                return typeof amount === 'number' ? <div className="text-nowrap fw-semibold">₹{parseFloat(amount).toLocaleString()}</div> : "-";
            }
        },
        {
            header: 'Total Transactions',
            cell: (_, item) => {
                const count = item.total_payment_transactions;
                return typeof count === 'number' ? <div className="text-nowrap">{count}</div> : "-";
            }
        },
        {
            header: 'Status',
            cell: (_, item) => (
                item.status ? (
                    <StatusChip
                        bg={getStatusBg(item.status)}
                        pill
                        title={item.status}
                        className="text-capitalize"
                    />
                ) : (
                    "-"
                )
            )
        },
        {
            header: 'Created By',
            cell: (_, item) => {
                if (item.created_by_type === 'admin' && item.creator_admin) {
                    return <div className="text-nowrap">Admin: {item.creator_admin.name}</div>;
                }
                if (item.created_by_type === 'user' && item.creator_user) {
                    return <div className="text-nowrap">User: {item.creator_user.first_name} {item.creator_user.last_name}</div>;
                }
                return "-";
            }
        },
        {
            header: 'Revoked By',
            cell: (_, item) => {
                if (item.status === 'revoked' && item.revoked_by) {
                    return <div className="text-nowrap text-danger fw-medium">Admin: {item.revoked_by.name || 'Admin'}</div>;
                }
                return "-";
            }
        },
        {
            header: 'Revoked At',
            cell: (_, item) => (
                item.status === 'revoked' && item.revoked_at ? (
                    <div className="text-nowrap">{formatMomentDate(item.revoked_at, "DD-MM-YYYY hh:mm A")}</div>
                ) : "-"
            )
        },
        {
            header: 'Last Payable Amount',
            cell: (_, item) => {
                const amount = item.payment_transaction?.payable_amount;
                return amount ? <div className="text-nowrap fw-semibold">₹{parseFloat(amount).toLocaleString()}</div> : "-";
            }
        },
        {
            header: 'Last Payment Method',
            cell: (_, item) => {
                const group = item.payment_transaction?.payment_group || item.payment_transaction?.payment_method?.type;
                return <div className="text-nowrap text-capitalize">{group || "-"}</div>;
            }
        },
        {
            header: 'Last Method Details',
            cell: (_, item) => {
                const tx = item.payment_transaction;
                if (!tx || (!tx.payment_details && !tx.payment_method)) return "-";
                const details = tx.payment_details || tx.payment_method;
                return <div className="text-nowrap small">{parsePaymentDetails(details, tx.payment_group)}</div>;
            }
        },
        {
            header: 'Last Payment ID',
            cell: (_, item) => {
                const tx = item.payment_transaction;
                if (!tx) return "-";
                const id = tx.cashfree_payment_id || tx.cf_payment_id || tx.payment_details?.payment_id;
                return <div className="text-nowrap">{id || "-"}</div>;
            }
        },
        {
            header: 'Last Payment Status',
            cell: (_, item) => {
                const status = item.payment_transaction?.status;
                return status ? (
                    <StatusChip
                        bg={getStatusBg(status)}
                        pill
                        title={status}
                        className="text-capitalize"
                    />
                ) : "-";
            }
        },
        {
            header: 'Last Paid At',
            cell: (_, item) => {
                const time = item.payment_transaction?.paid_at || item.payment_transaction?.payment_time;
                return time ? <div className="text-nowrap">{formatMomentDate(time, "DD-MM-YYYY hh:mm A")}</div> : "-";
            }
        },
        {
            header: 'Last Error Details',
            cell: (_, item) => {
                const err = item.payment_transaction?.error_details;
                if (!err) return "-";
                return <div className="text-nowrap small text-danger" style={{ maxWidth: "200px", overflow: "hidden", textOverflow: "ellipsis" }} title={typeof err === 'string' ? err : JSON.stringify(err)}>{typeof err === 'string' ? err : "See error object"}</div>;
            }
        }
    ];

    const planOptions = plans
        .filter(plan => plan.plan_type !== "free")
        .map(plan => ({
            value: plan.id,
            label: `${plan.name} (${plan.interval})`
        }));

    const paginationConfig = {
        currentPage,
        totalPages: Math.ceil(total / limit),
        total: total,
        onPageChange: (page) => setCurrentPage(page),
        pageSize: limit,
        setPageSize: (newLimit) => setLimit({ value: newLimit, label: newLimit }),
        setCurrentPage,
        perPageOptions: [10, 20, 50, 100],
    };

    return (
        <>
            {headerPortalTarget && createPortal(headerContent, headerPortalTarget)}
            <div className="p-3 d-flex flex-wrap align-items-end column-gap-3 row-gap-2 bg-white">
                {/* Search */}
                <TableSearch value={searchTerm}
                    placeholder="Search by user name, email or mobile number"
                    onChange={(e) => {
                        setSearchTerm(e.target.value);
                        setCurrentPage(1);
                    }} />

                {/* Status Filter */}
                <CustomSelect
                    label={{ name: "Status", class: "mb-0" }}
                    options={statusOptions}
                    value={selectedStatuses}
                    isMulti
                    closeMenuOnSelect={false}
                    hideSelectedOptions={false}
                    selectProps={{
                        components: { Option: CheckboxOption, MultiValue: MultiValueMessage },
                        styles: customStyles
                    }}
                    placeholder="All"
                    onChange={(selected) => {
                        setSelectedStatuses(selected || []);
                        setCurrentPage(1);
                    }}
                    style={{ width: '160px' }}
                />

                {/* Source Filter */}
                <CustomSelect
                    label={{ name: "Source", class: "mb-0" }}
                    options={sourceOptions}
                    value={selectedSources}
                    isMulti
                    closeMenuOnSelect={false}
                    hideSelectedOptions={false}
                    selectProps={{
                        components: { Option: CheckboxOption, MultiValue: MultiValueMessage },
                        styles: customStyles
                    }}
                    placeholder="All"
                    onChange={(selected) => {
                        setSelectedSources(selected || []);
                        setCurrentPage(1);
                    }}
                    style={{ width: '160px' }}
                />

                {/* Subscription Plan Filter */}
                <CustomSelect
                    label={{ name: "Subscription Plan", class: "mb-0" }}
                    options={planOptions}
                    value={selectedPlans}
                    isMulti
                    closeMenuOnSelect={false}
                    hideSelectedOptions={false}
                    selectProps={{
                        components: { Option: CheckboxOption, MultiValue: MultiValueMessage },
                        styles: customStyles
                    }}
                    placeholder="All"
                    onChange={(selected) => {
                        setSelectedPlans(selected || []);
                        setCurrentPage(1);
                    }}
                    style={{ width: '220px' }}
                />
            </div>

            <CustomTable
                data={data}
                columns={columns}
                keyField="id"
                actionList={actionList}
                emptyMessage="No subscriptions found."
                tbody={{ trClass: "" }}
                pagination={paginationConfig}
            />

            <ConfirmModal
                isOpen={showReversePopup}
                onClose={() => {
                    setShowReversePopup(false);
                    setSubscriptionToReverse(null);
                }}
                title="Revoke Plan"
                message="Are you sure you want to revoke this plan?"
                onConfirm={handleReverseConfirm}
            />
        </>
    );
};

export default List;
